// Exact types matching backend API contracts
// CRITICAL: These must match backend database schema and API responses exactly

// Enum types (must match backend exactly)
export type AnnouncementType = 'meeting' | 'birthday' | 'event' | 'memorial' | 'fundraiser' | 'general';
export type AnnouncementPriority = 'critical' | 'important' | 'general' | 'memorial';
export type AnnouncementVisibility = 'public' | 'authenticated' | 'members' | 'admins';
export type AnnouncementStatus = 'draft' | 'scheduled' | 'published' | 'archived';
export type InteractionType = 'view' | 'like' | 'love' | 'celebrate' | 'support' | 'rsvp_yes' | 'rsvp_no' | 'bookmark' | 'share';

// Attachment type (matches backend JSON schema)
export interface AnnouncementAttachment {
  name: string;
  url: string;
  size: string;
  type: string;
}

// Base Announcement interface (matches backend database schema exactly)
export interface Announcement {
  id: string;
  title: string;
  content: string;
  excerpt: string | null;
  type: AnnouncementType;
  priority: AnnouncementPriority;
  visibility: AnnouncementVisibility;
  status: AnnouncementStatus;
  
  // Date fields - backend returns ISO strings, need to convert to Date objects
  startDate: string | null;
  endDate: string | null;
  publishedAt: string | null;
  scheduledFor: string | null;
  createdAt: string;
  updatedAt: string;
  
  // Metadata
  isPinned: boolean;
  isFeatured: boolean;
  viewCount: number;
  
  // Notification settings
  notifyChannels: string[] | null; // ['email', 'sms', 'whatsapp']
  notifyOnPublish: boolean;
  
  // Attachments
  images: string[] | null; // Array of URLs
  attachments: AnnouncementAttachment[] | null;
  
  // Audit fields
  createdBy: string;
  updatedBy: string | null;
}

// Interaction counts (matches backend repository interface exactly)
export interface AnnouncementInteractions {
  likes: number;
  loves: number;
  celebrates: number;
  supports: number;
  rsvpYes: number;
  rsvpNo: number;
  views: number;
  bookmarks: number;
  shares: number;
}

// Full announcement with interactions (matches backend AnnouncementWithInteractions)
export interface AnnouncementWithInteractions extends Announcement {
  interactions: AnnouncementInteractions;
  userInteractions?: InteractionType[]; // Only present when user is authenticated
}

// Pagination interface (matches backend pagination response exactly)
export interface PaginationInfo {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

// Re-export canonical ApiResponse and type guards
export type { ApiResponse } from './index';
export { isApiSuccess, isApiError } from './index';

// Specific API response types (matches backend handlers exactly)
export interface GetAnnouncementsResponse {
  announcements: AnnouncementWithInteractions[];
  pagination: PaginationInfo;
}

export interface GetPublicAnnouncementsResponse {
  announcements: AnnouncementWithInteractions[];
  pagination: PaginationInfo;
}

export interface GetAnnouncementResponse {
  announcement: AnnouncementWithInteractions;
}

export interface CreateAnnouncementResponse {
  announcement: Announcement;
}

export interface UpdateAnnouncementResponse {
  announcement: Announcement;
}

export interface PublishAnnouncementResponse {
  announcement: Announcement;
}

export interface AddInteractionResponse {
  announcement: AnnouncementWithInteractions;
  interaction: {
    type: InteractionType;
    added: boolean;
  };
}

// Request interfaces for API calls
export interface GetAnnouncementsRequest {
  type?: AnnouncementType;
  priority?: AnnouncementPriority | AnnouncementPriority[];
  visibility?: AnnouncementVisibility | AnnouncementVisibility[];
  status?: AnnouncementStatus | AnnouncementStatus[];
  isPinned?: boolean;
  isFeatured?: boolean;
  dateFrom?: string; // ISO string
  dateTo?: string; // ISO string
  search?: string;
  limit?: number;
  offset?: number;
  orderBy?: 'createdAt' | 'publishedAt' | 'priority' | 'title';
  orderDirection?: 'asc' | 'desc';
}

export interface GetPublicAnnouncementsRequest {
  featured?: boolean;
  limit?: number;
  offset?: number;
}

export interface CreateAnnouncementRequest {
  title: string;
  content: string;
  excerpt?: string;
  type: AnnouncementType;
  priority?: AnnouncementPriority;
  visibility?: AnnouncementVisibility;
  status?: AnnouncementStatus;
  startDate?: string; // ISO string
  endDate?: string; // ISO string
  scheduledFor?: string; // ISO string
  isPinned?: boolean;
  isFeatured?: boolean;
  notifyChannels?: string[];
  notifyOnPublish?: boolean;
  images?: string[];
  attachments?: AnnouncementAttachment[];
}

export interface UpdateAnnouncementRequest {
  title?: string;
  content?: string;
  excerpt?: string;
  type?: AnnouncementType;
  priority?: AnnouncementPriority;
  visibility?: AnnouncementVisibility;
  startDate?: string; // ISO string
  endDate?: string; // ISO string
  scheduledFor?: string; // ISO string
  isPinned?: boolean;
  isFeatured?: boolean;
  notifyChannels?: string[];
  notifyOnPublish?: boolean;
  images?: string[];
  attachments?: AnnouncementAttachment[];
}

export interface AddInteractionRequest {
  interactionType: InteractionType;
  interactionData?: Record<string, unknown>;
}


// Utility functions for date conversion
export function convertAnnouncementDates(announcement: any): AnnouncementWithInteractions {
  return {
    ...announcement,
    startDate: announcement.startDate ? new Date(announcement.startDate).toISOString() : null,
    endDate: announcement.endDate ? new Date(announcement.endDate).toISOString() : null,
    publishedAt: announcement.publishedAt ? new Date(announcement.publishedAt).toISOString() : null,
    scheduledFor: announcement.scheduledFor ? new Date(announcement.scheduledFor).toISOString() : null,
    createdAt: new Date(announcement.createdAt).toISOString(),
    updatedAt: new Date(announcement.updatedAt).toISOString(),
  };
}